import {
  MoreHorizontalIcon,
  PencilIcon,
  PowerIcon,
  Trash2Icon,
  ZapIcon,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useConnections } from '@/features/connections';
import { useWorkflows } from '@/features/workflows';
import type { TriggerProfileView } from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';
import {
  LIST_ROW,
  LIST_ROW_NAME,
  LIST_ROW_SECONDARY,
  LIST_ROW_TRAILING,
  LIST_ROWS,
} from '@/shared/components/listRow';

import {
  JiraTriggerConfigSchema,
  ScheduledTriggerConfigSchema,
  WorkspaceTaskChangeConfigDisplaySchema,
  withDefaultedMode,
} from '../types';
import { resolveStepName } from '../workflowSteps';
import { useCanWork } from '@/features/workspaces/canWork';

function isScheduled(profile: TriggerProfileView): boolean {
  return (
    profile.type === 'github' ||
    profile.type === 'gitlab' ||
    profile.type === 'jira'
  );
}

/** "Create task" / "Continue task", or `null` for a profile whose stored
 *  `config` doesn't parse — same fallback posture as the detail view
 *  (research.md D7): never guess, never throw, just say less. Parses
 *  against whichever schema the profile's own `type` calls for, using the
 *  permissive read-path schema for `workspace_task_change` (Spec 082) so a
 *  legacy `continue_task` profile still labels correctly rather than
 *  falling into the parse-failure fallback. */
function actionKindLabel(profile: TriggerProfileView): string | null {
  const parsed =
    profile.type === 'jira'
      ? JiraTriggerConfigSchema.safeParse(profile.config)
      : isScheduled(profile)
        ? ScheduledTriggerConfigSchema.safeParse(profile.config)
        : WorkspaceTaskChangeConfigDisplaySchema.safeParse(
            withDefaultedMode(profile.config),
          );
  if (!parsed.success) return null;
  return parsed.data.action.kind === 'create_task'
    ? 'Create task'
    : 'Continue task';
}

/** A concise cadence string — `"every 300s"` or `"cron 0 * * * * (UTC)"` —
 *  for a scheduled profile's row. Never resolves to nothing: even an
 *  incompletely-configured profile (schedule fields still null) says so
 *  plainly rather than rendering a blank detail line. */
function scheduleLabel(profile: TriggerProfileView): string {
  if (profile.schedule_type === 'cron') {
    return `cron ${profile.cron_expression ?? '—'} (${profile.timezone ?? '—'})`;
  }
  return `every ${profile.interval_seconds ?? '—'}s`;
}

/**
 * Every trigger profile in the workspace, one row each (FR-005): name,
 * action type, the watched transition (or, for a scheduled GitHub/GitLab
 * profile, its connection and cadence — FR concise-context), and whether it
 * is enabled. Enable/Disable, Edit and Delete sit in the row menu (spec 129)
 * — still no dialog, no re-entering the rest of the configuration.
 *
 * A `workspace_task_change` row's shape and behavior are byte-for-byte
 * unchanged from Spec 060 (FR-016, SC-005) — only a scheduled profile's row
 * renders a different detail line.
 *
 * Rows on the shared recipe (spec 229 research R8): icon leading, name/detail
 * stacked, state chip and row menu trailing, the card around them the only
 * border — the same row Settings → People draws.
 */
export function TriggerProfileList({
  workspaceId,
  profiles,
  onToggleEnabled,
  togglingId,
  onSelect,
  onEdit,
  onDelete,
}: {
  workspaceId: string;
  profiles: TriggerProfileView[];
  onToggleEnabled: (profile: TriggerProfileView) => void;
  /** The one profile currently mid-toggle, if any — disables just that row's
   *  control rather than the whole list while its mutation is in flight. */
  togglingId: string | null;
  /** Opens the row's detail view (US3, T024). A `<button>` around the
   *  name/detail block, not a click handler on the whole `<li>` — the row
   *  menu is its own button right beside it, and this avoids the two ever
   *  needing to coordinate over a bubbled click. */
  onSelect: (profile: TriggerProfileView) => void;
  /** Opens the edit form, pre-populated (spec 074 US1). */
  onEdit: (profile: TriggerProfileView) => void;
  /** Opens the delete confirmation dialog (spec 074 US2). */
  onDelete: (profile: TriggerProfileView) => void;
}) {
  const { workflows } = useWorkflows(workspaceId);
  const canWork = useCanWork();
  const { connections } = useConnections(workspaceId);

  return (
    <ul aria-label="Triggers" className={LIST_ROWS}>
      {profiles.map((profile) => {
        const scheduled = isScheduled(profile);
        const kindLabel = actionKindLabel(profile);

        let detailLabel: string;
        let workflowName: string | undefined;
        if (scheduled) {
          const connection = (connections ?? []).find(
            (c) => c.id === profile.connection_id,
          );
          detailLabel = `${connection?.name ?? profile.connection_id ?? 'No connection'} · ${scheduleLabel(profile)}`;
        } else {
          const parsed = WorkspaceTaskChangeConfigDisplaySchema.safeParse(
            withDefaultedMode(profile.config),
          );
          if (!parsed.success) {
            detailLabel = 'Configuration in an unexpected shape';
          } else if (parsed.data.mode === 'schedule') {
            // Schedule mode (Spec 082) has no watched transition to show —
            // the same cadence line a scheduled github/gitlab row shows,
            // reusing `scheduleLabel` rather than a bespoke format.
            detailLabel = scheduleLabel(profile);
          } else {
            const { workflow_id, from_step_id, to_step_id } = parsed.data;
            const workflow = workflows.find((wf) => wf.id === workflow_id);
            workflowName = workflow?.name;
            detailLabel = `${resolveStepName(workflow, from_step_id)} → ${resolveStepName(workflow, to_step_id)}`;
          }
        }

        return (
          <li
            key={profile.id}
            className={cn(LIST_ROW, 'hover:bg-muted/50 transition-colors')}
          >
            <ZapIcon
              aria-hidden="true"
              className="text-muted-foreground size-5 shrink-0"
              strokeWidth={1.5}
            />
            <button
              type="button"
              className="flex min-w-0 flex-1 flex-col gap-0.5 text-left"
              onClick={() => {
                onSelect(profile);
              }}
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className={LIST_ROW_NAME}>{profile.name}</span>
                {kindLabel ? (
                  <Badge variant="secondary" className="shrink-0">
                    {kindLabel}
                  </Badge>
                ) : null}
              </span>
              <span
                className={cn(LIST_ROW_SECONDARY, 'max-sm:whitespace-normal')}
              >
                {workflowName ? `${workflowName}: ` : ''}
                {detailLabel}
              </span>
            </button>
            <span className={LIST_ROW_TRAILING}>
              {/* A state, not a control; the verbs sit in the row menu, the
                shape every settings list now shares (spec 129 FR-002). */}
              <Badge
                variant={profile.enabled ? 'outline' : 'secondary'}
                className="shrink-0"
              >
                {profile.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
              {canWork ? (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button
                        variant="ghost"
                        size="icon"
                        className="touch-target shrink-0"
                      >
                        <MoreHorizontalIcon aria-hidden="true" />
                        <span className="sr-only">
                          Actions for {profile.name}
                        </span>
                      </Button>
                    }
                  />
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      disabled={togglingId === profile.id}
                      onClick={() => {
                        onToggleEnabled(profile);
                      }}
                    >
                      <PowerIcon aria-hidden="true" />
                      {profile.enabled ? 'Disable' : 'Enable'}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        onEdit(profile);
                      }}
                    >
                      <PencilIcon aria-hidden="true" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={() => {
                        onDelete(profile);
                      }}
                    >
                      <Trash2Icon aria-hidden="true" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
